"use client";

import dayjs from "@calcom/dayjs";
import { useBookerStoreContext } from "@calcom/features/bookings/Booker/BookerStoreProvider";
import { useBookerTime } from "@calcom/features/bookings/Booker/hooks/useBookerTime";
import type { BookerEvent } from "@calcom/features/bookings/types";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { eventTypeMetaDataSchemaWithoutApps } from "@calcom/prisma/zod-utils";
import { Button } from "@calcom/ui/components/button";
import { Dialog, DialogClose, DialogContent, DialogFooter } from "@calcom/ui/components/dialog";
import { Label, TextField } from "@calcom/ui/components/form";
import { RadioField, RadioGroup } from "@calcom/ui/components/radio";
import { type FormEvent, useState } from "react";

type Scope = "DAY" | "WEEK";
type Outcome = "created" | "already_registered" | "slots_available";

type Props = {
  event?: Pick<BookerEvent, "id" | "metadata"> | null;
};

type ScopeDates = { day: Date; weekFrom: Date; weekTo: Date };

/**
 * Las mismas fechas que cubre la inscripción en el servidor (computeWaitlistRange), para que cada
 * opción diga exactamente qué días incluye. Son fechas sin hora: se calculan y formatean en UTC.
 */
function scopeDates(date: string, today: string): ScopeDates | null {
  const day = dayjs.utc(date);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !day.isValid()) return null;
  const monday = day.subtract((day.day() + 6) % 7, "day");
  const weekFrom = monday.format("YYYY-MM-DD") < today ? dayjs.utc(today) : monday;
  return {
    day: day.toDate(),
    weekFrom: weekFrom.toDate(),
    weekTo: monday.add(6, "day").toDate(),
  };
}

/**
 * El selector de fecha nativo solo se abre con el icono del calendario; así se abre con un clic en
 * cualquier parte del campo o en su etiqueta (que reenvía el clic al campo). Safari viejo no tiene
 * showPicker y lanza si el selector ya está abierto: en ambos casos basta con no hacer nada.
 */
function openNativePicker(input: HTMLInputElement) {
  try {
    input.showPicker?.();
  } catch {
    // El campo sigue funcionando a mano.
  }
}

const dateFormat = (language: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(language, { ...options, timeZone: "UTC" });

/**
 * Helima: entrada a la lista de espera debajo del calendario. Va siempre visible (si el evento la
 * activó) y no solo en el estado "sin cupos" porque el calendario deshabilita los días llenos: quien
 * busca un día concreto nunca llegaría a ver ese estado.
 */
export function WaitlistPrompt({ event }: Props) {
  const { t, i18n } = useLocale();
  const { timezone } = useBookerTime();
  const selectedDate = useBookerStoreContext((state) => state.selectedDate);
  const month = useBookerStoreContext((state) => state.month);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [scope, setScope] = useState<Scope>("WEEK");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const metadata = eventTypeMetaDataSchemaWithoutApps.safeParse(event?.metadata);
  if (!event || !metadata.success || !metadata.data?.helimaWaitlist?.enabled) return null;

  const today = dayjs().tz(timezone).format("YYYY-MM-DD");

  const openDialog = () => {
    const monthStart = month ? `${month}-01` : today;
    setDate(selectedDate ?? (monthStart > today ? monthStart : today));
    setScope("WEEK");
    setError(null);
    setOutcome(null);
    setOpen(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch("/api/helima/lista-de-espera", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventTypeId: event.id,
          name,
          email,
          timeZone: timezone,
          date,
          scope: effectiveScope,
        }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        setError(body?.message ?? t("waitlist_error"));
        return;
      }
      setOutcome(body.status);
    } catch {
      setError(t("waitlist_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const language = i18n.language;
  const dates = scopeDates(date, today);
  const scopeOptions: { value: Scope; label: string }[] = [];
  if (dates) {
    const dayLabel = dateFormat(language, { weekday: "long", day: "numeric", month: "long" })
      .format(dates.day)
      .replace(",", "");
    scopeOptions.push({ value: "DAY", label: t("waitlist_scope_day", { day: dayLabel }) });
    // Un domingo (o el último día de una semana ya empezada) no tiene "semana" que ofrecer aparte del día.
    if (dates.weekFrom < dates.weekTo) {
      const sameMonth = dates.weekFrom.getUTCMonth() === dates.weekTo.getUTCMonth();
      scopeOptions.push({
        value: "WEEK",
        label: t("waitlist_scope_range", {
          from: dateFormat(
            language,
            sameMonth ? { day: "numeric" } : { day: "numeric", month: "long" }
          ).format(dates.weekFrom),
          to: dateFormat(language, { day: "numeric", month: "long" }).format(dates.weekTo),
        }),
      });
    }
  }
  const effectiveScope =
    scopeOptions.some((option) => option.value === scope) || scopeOptions.length === 0 ? scope : "DAY";

  const outcomeText: Record<Outcome, string> = {
    created: t("waitlist_created"),
    already_registered: t("waitlist_already_registered"),
    slots_available: t("waitlist_slots_available"),
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="text-subtle hover:text-emphasis mt-1 w-full text-center text-sm underline underline-offset-2">
        {t("waitlist_prompt")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          title={t("waitlist_title")}
          description={t("waitlist_description")}
          type="creation"
          className="sm:max-w-md">
          {outcome ? (
            <>
              <p className="text-default text-sm">{outcomeText[outcome]}</p>
              <DialogFooter noSticky>
                <DialogClose color="primary">{t("close")}</DialogClose>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={submit}>
              <div className="space-y-4">
                <TextField
                  name="waitlist-name"
                  label={t("your_name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={120}
                />
                <TextField
                  name="waitlist-email"
                  label={t("email_address")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  maxLength={254}
                />
                <TextField
                  name="waitlist-date"
                  label={t("waitlist_date")}
                  type="date"
                  value={date}
                  min={today}
                  onChange={(e) => setDate(e.target.value)}
                  onClick={(e) => openNativePicker(e.currentTarget)}
                  required
                />
                {dates ? (
                  <div className="pt-2">
                    <Label>{t("waitlist_scope")}</Label>
                    <RadioGroup
                      value={effectiveScope}
                      onValueChange={(value: string) => setScope(value as Scope)}
                      className="space-y-1">
                      {scopeOptions.map((option) => (
                        <RadioField
                          key={option.value}
                          id={`waitlist-scope-${option.value}`}
                          value={option.value}
                          label={option.label}
                          withPadding
                        />
                      ))}
                    </RadioGroup>
                  </div>
                ) : null}
                {error ? <p className="text-error text-sm">{error}</p> : null}
              </div>
              <DialogFooter noSticky>
                <DialogClose />
                <Button type="submit" loading={submitting}>
                  {t("waitlist_submit")}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
